'use client'

import { useEffect, useState } from 'react'
import { MC, SERIF } from '@/lib/mycio/theme'
import { CASE_SHELF, CASE_SHELF_NOTE, type ShowcaseCase } from './caseShowcase'

/**
 * The CFP case shelf: cases we did not write, and the plans the engine produced for them.
 *
 * THE CARDS ARE PLANS, NOT PDFs — deliberately. A shelf of document thumbnails is what the 1%
 * advisor emails once a year and nobody opens, and this page is positioned against exactly that.
 * Each card reads as a page from a living plan, and the panel behind it goes synopsis -> the
 * letter the engine wrote -> the whole plan -> the original case at the FPA. Increasing depth,
 * with the third-party question always one click away so the reader can check the facts were not
 * chosen to suit us.
 *
 * See `caseShowcase.ts` for what may and may not be published here — in short: link the case,
 * never rehost it; no FPA marks; no comparison against the competition's finalist plans; and never
 * claim to have passed or scored, because our own measurement is too noisy to defend in public.
 */
export default function CaseShelf() {
  const [open, setOpen] = useState<ShowcaseCase | null>(null)
  return (
    <>
      <div
        className="mc-case-shelf no-print"
        style={{
          display: 'flex', gap: 20, overflowX: 'auto', overflowY: 'hidden',
          paddingBlock: '4px 22px', scrollSnapType: 'x mandatory',
          // FULL BLEED, THEN RE-ALIGN. `50% - 50vw` is half the parent's content width minus half
          // the viewport, i.e. exactly the gap between this container's edge and the screen edge —
          // so the row runs to both edges and reads as scrollable rather than clipped. The padding
          // then puts the FIRST card back on the container's text edge: the section is max-width
          // 1180 with 32px padding, so that edge sits at (100vw - 1180)/2 + 32 = 50vw - 558.
          marginInline: 'calc(50% - 50vw)', paddingInline: 'max(20px, calc(50vw - 558px))',
        }}
      >
        {CASE_SHELF.map((c) => (
          <button
            key={c.year}
            type="button"
            onClick={() => setOpen(c)}
            aria-label={`${c.title} — ${c.year} case study`}
            style={{
              scrollSnapAlign: 'start', flex: '0 0 300px', textAlign: 'left', cursor: 'pointer',
              background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 14,
              padding: 0, overflow: 'hidden', color: MC.ink,
              boxShadow: '0 18px 44px -30px rgba(35,33,28,0.5)',
            }}
          >
            {/* The card head reads like the top of a plan, not the cover of a report. */}
            <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${MC.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: MC.faint2, fontWeight: 700 }}>
                  {c.year} case study
                </span>
                {c.cold && (
                  <span style={{ fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: MC.forest, background: '#DDE8E0', borderRadius: 999, padding: '3px 9px' }}>
                    Run cold
                  </span>
                )}
              </div>
              <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '8px 0 0', letterSpacing: '-0.01em' }}>
                {c.title}
              </h3>
            </div>
            <div style={{ padding: '14px 20px 18px' }}>
              <p style={{ fontSize: 14, lineHeight: 1.5, color: MC.inkSoft, margin: '0 0 14px', textWrap: 'pretty' }}>
                {c.standfirst}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {c.demands.slice(0, 3).map((d) => (
                  <li key={d} style={{ fontSize: 12.5, lineHeight: 1.45, color: MC.faint, paddingLeft: 14, position: 'relative' }}>
                    <span aria-hidden style={{ position: 'absolute', left: 0, color: MC.forest }}>·</span>
                    {d}
                  </li>
                ))}
              </ul>
              <span style={{ display: 'inline-block', marginTop: 16, fontSize: 13.5, fontWeight: 600, color: MC.forest }}>
                See the case →
              </span>
            </div>
          </button>
        ))}
      </div>

      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: MC.faint2, margin: '4px 0 0', maxWidth: 760 }}>
        {CASE_SHELF_NOTE}
      </p>

      {open && <CasePanel c={open} onClose={() => setOpen(null)} />}
    </>
  )
}

function CasePanel({ c, onClose }: { c: ShowcaseCase; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${c.title}, ${c.year} case study`}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(26,42,38,0.55)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 680, maxHeight: '86vh', background: MC.surface,
          borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 40px 90px -30px rgba(26,42,38,0.6)', color: MC.ink,
        }}
      >
        <div style={{ flex: 'none', padding: '24px 30px 18px', borderBottom: '1px solid #E4DCCB', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <p style={{ fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: MC.faint, fontWeight: 700, margin: '0 0 5px' }}>
              {c.year} Financial Planning Challenge case study
            </p>
            <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 28, letterSpacing: '-0.01em', margin: 0 }}>{c.title}</h2>
            <p style={{ fontSize: 12.5, color: MC.faint2, margin: '6px 0 0' }}>{c.standfirst}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ flex: 'none', width: 36, height: 36, borderRadius: 999, background: '#EEE8DC', border: 'none', color: MC.muted, fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>
            ✕
          </button>
        </div>

        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '24px 30px 28px' }}>
          {/* 1. The situation, in our words — never the case author's prose. */}
          <Block h="The situation">
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: MC.inkSoft, margin: 0, textWrap: 'pretty' }}>{c.synopsis}</p>
          </Block>

          <Block h="What it turns on">
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {c.demands.map((d) => (
                <li key={d} style={{ fontSize: 14, lineHeight: 1.5, color: MC.inkSoft }}>{d}</li>
              ))}
            </ul>
          </Block>

          {/* 2. The computational demonstration: obvious strategy -> probability -> what the search found ->
              the lever -> the improvement -> assumptions and what isn't supported. Rendered field by field,
              only from a recorded run (see `demo` in caseShowcase.ts). */}
          {c.demo && (
            <>
              {c.demo.obviousStrategy && (
                <Block h="The obvious strategy">
                  <p style={DEMO_P}>{c.demo.obviousStrategy}{c.demo.obviousProbability ? ` (${c.demo.obviousProbability})` : ''}</p>
                </Block>
              )}
              {c.demo.optimizerFound && (
                <Block h="What the search found">
                  <p style={DEMO_P}>{c.demo.optimizerFound}</p>
                </Block>
              )}
              {c.demo.lever && (
                <Block h="The lever">
                  <p style={DEMO_P}>{c.demo.lever}</p>
                </Block>
              )}
              {c.demo.improvement && (
                <Block h="The improvement">
                  <p style={DEMO_P}>{c.demo.improvement}</p>
                </Block>
              )}
              {!!c.demo.assumptions?.length && (
                <Block h="Assumptions">
                  <ul style={DEMO_UL}>{c.demo.assumptions.map((a) => <li key={a} style={DEMO_LI}>{a}</li>)}</ul>
                </Block>
              )}
              {!!c.demo.notSupported?.length && (
                <Block h="What MyCIO doesn’t model here">
                  <ul style={DEMO_UL}>{c.demo.notSupported.map((a) => <li key={a} style={DEMO_LI}>{a}</li>)}</ul>
                </Block>
              )}
            </>
          )}

          {/* 3. The letter the engine wrote. Only rendered when a real run has supplied it — an
              empty quote block would be worse than no quote block. */}
          {c.letter && (
            <Block h="From the plan our engine wrote">
              <blockquote style={{ margin: 0, padding: '14px 18px', borderLeft: `3px solid ${MC.forest}`, background: MC.bg, borderRadius: '0 10px 10px 0' }}>
                <p style={{ fontSize: 14.5, lineHeight: 1.65, color: MC.ink, margin: 0, whiteSpace: 'pre-wrap', textWrap: 'pretty' }}>{c.letter}</p>
              </blockquote>
            </Block>
          )}
        </div>

        {/* 4. and 5. The whole plan, and the question it was answering. */}
        <div style={{ flex: 'none', padding: '16px 30px 22px', borderTop: '1px solid #E4DCCB', background: MC.bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <a href={c.caseUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 13.5, color: MC.forest, fontWeight: 600, textDecoration: 'none' }}>
            Read the original case (FPA, PDF) ↗
          </a>
          {c.planUrl
            ? (
              <a href={c.planUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 14.5, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '11px 22px', borderRadius: 10, textDecoration: 'none' }}>
                Open the full plan
              </a>
            )
            : (
              <span style={{ fontSize: 13, color: MC.faint2 }}>Worked result publishing shortly</span>
            )}
        </div>
      </div>
    </div>
  )
}

const DEMO_P: React.CSSProperties = { fontSize: 14.5, lineHeight: 1.6, color: MC.inkSoft, margin: 0, textWrap: 'pretty' }
const DEMO_UL: React.CSSProperties = { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }
const DEMO_LI: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, color: MC.inkSoft }

function Block({ h, children }: { h: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h3 style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: MC.faint2, margin: '0 0 8px' }}>{h}</h3>
      {children}
    </div>
  )
}
